import React, { useEffect, useMemo, useState } from 'react';
import { BwjPageContent } from '../../../../framework/components/bwj-page-content';
import { BwjPageHeader } from '../../../../framework/components/bwj-page-header';
import { BwjViewManager, BwjViewState } from '../../../../framework/components/bwj-view-manager';
import { useParams } from 'react-router-dom';
import * as formService from '../../form-services/publish-door';
import { ILowesStormDoorEditingModel, LowesStormDoorEditingModel } from '../../../../entities-generated/models/lowes-storm-door/lowes-storm-door-editing-model';
import { AppRoutes } from '../../../app-routes';
import { BwjGrid, Col, Row } from '../../../../framework/components/bwj-layout';
import { Spacing } from '../../../../framework/components/bwj-layout/styles';
import { BwjTextField } from '../../../../framework/components/bwj-text-field';
import { LowesStormDoorApiClient } from '../../../../services/lowes-storm-door';
import { BwjSingleSelectField } from '../../../../framework/components/bwj-select-field/single';
import { IDoorSizeOption } from '../../../../entities-generated/common/door-size-option';
import { IStormDoorHandleTypeOption } from '../../../../entities-generated/common/storm-door-handle-type-option';
import { BwjCheckbox } from '../../../../framework/components/bwj-checkbox';
import { MessageBarType, Text } from '@fluentui/react';
import { BwjImageBrowser, IBrowserImage } from '../../../../framework/components/bwj-image-browser';
import { remove } from '../../../../framework/utils/collection';
import { LowesProductVariantListItem } from '../../components/variant-list-item';
import { ILowesProductTexture } from '../../../../entities-generated/common/lowes/lowes-product-texture';
import { StormDoorColor } from '../../../../entities-generated/common/storm-door-color';
import { useNavigator } from '../../../../framework/services/navigation';
import { useToast } from '../../../../framework/components/bwj-toast/context';
import { BwjConfirmationDialog, IConfirmationDialogDisplayProps } from '../../../../framework/components/bwj-confirmation-dialog';
import { BwjEventArgs } from '../../../../framework/entities/bwj-event';

const subscriberId = 'Page.PublishLowesStormDoor';

export const PagePublishLowesStormDoor: React.FunctionComponent = () => {
  const { id: doorId } = useParams<{id: string}>();
  const nav = useNavigator();
  const { showToast } = useToast();
  const formModel = formService.useFormModel(subscriberId);
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const [door, setDoor] = useState<ILowesStormDoorEditingModel>(new LowesStormDoorEditingModel());
  const [primaryImageDisplayIndex, setPrimaryImageDisplayIndex] = useState(0);
  const [displayProps, setDisplayProps] = useState<BwjEventArgs<IConfirmationDialogDisplayProps | undefined> | undefined>(undefined);
  const productBaseUrl = useMemo(() => `https://lowes.com${door.vendorUrl.replace(/\/[a-z0-9]+$/, '')}`, [door]);
  const colorFamilyHint = useMemo(() => {
    const validValues = door.colorFamilies.map(f => StormDoorColor[f.id]);
    return `Valid values are ${validValues.join(', ')}`;
  }, [door]);
  
  useEffect(() => {
    LowesStormDoorApiClient.getImportedStormDoor(doorId)
    .then(d => {
      setDoor(d);
      formService.initializeService(d);
      setViewState(BwjViewState.Ready);
    })
    .catch(() => setViewState(BwjViewState.Error));
  }, [doorId])

  const onPublishStormDoor = () => {
    formService.validateAndUpdateModel()
    .then(result => {
      if(result.isValid === false) { return null; }
      
      setViewState(BwjViewState.Loading);
      const request = formService.toPublishRequest();
      return LowesStormDoorApiClient.publishLowesStormDoor(request, doorId);
    })
    .then(resp => {
      if(!resp) { return; }
      showToast(MessageBarType.success, `Storm door '${formModel.description.value}' successfully published`);
      nav.goTo(AppRoutes.importedLowesStormDoors.url());
    })
    .catch(() => {
      setViewState(BwjViewState.Ready);
      showToast(MessageBarType.error, 'An unexpected error occurred');
    });
  }

  const onProceedWithRejection = () => {
    setDisplayProps(undefined);
    setViewState(BwjViewState.Loading);

    const request = formService.toPublishRejectionRequest();
    LowesStormDoorApiClient.publishLowesStormDoor(request, doorId)
    .then(resp => {
      showToast(MessageBarType.info, `Storm door '${formModel.description.value}' was rejected`);
      nav.goTo(AppRoutes.importedLowesStormDoors.url());
    })
    .catch(() => {
      setViewState(BwjViewState.Ready);
      showToast(MessageBarType.error, 'An unexpected error occurred');
    });
  }

  const onRejectStormDoor = () => {
    const dp = {
      title: 'Reject this Storm Door',
      message: 'Are you sure you want to block this and future publishing of this product?',
      proceedPrompt: 'Yes, reject',
      cancelPrompt: 'Cancel',
      onProceedRequested: onProceedWithRejection,
      onCancelRequested: () => {}
    };
    setDisplayProps(new BwjEventArgs(dp));
  }

  const onViewOnVendorSite = () => {
    window.open(`https://lowes.com${door.vendorUrl}`, '_blank');
  }

  const onPrimaryImageRemoved = (image: IBrowserImage) => {
    const imageUrls = remove(formModel.imageUrls.value, i => i === image.src);
    if(primaryImageDisplayIndex >= imageUrls.length) {
      setPrimaryImageDisplayIndex(imageUrls.length - 1);
    }
    formService.imageUrls.onUpdate(imageUrls);
  }

  const onVariantSelected = (variant: ILowesProductTexture) => {
    window.open(`${productBaseUrl}/${variant.omniItemId}`, '_blank');
  }

  return (
    <BwjViewManager
      state={viewState}
      loadingState={{
        message: 'Loading content...'
      }}
      errorState={{
        message: 'An unexpected error occurred'
      }}
    >
      <BwjConfirmationDialog displayArgs={displayProps} />
      <BwjPageHeader
        title={door.description}
        titleIconName={AppRoutes.publishLowesStormDoor.icon}
        actionButtons={[{
          id: 'btnPublish',
          name: 'Publish door',
          iconName: 'PublishContent',
          onClick: onPublishStormDoor,
        }, {
          id: 'btnReject',
          name: 'Reject door',
          iconName: 'Blocked',
          onClick: onRejectStormDoor,
        }, {
          id: 'btnViewAtVendor',
          name: 'View on vendor site',
          iconName: 'Globe',
          onClick: onViewOnVendorSite,
        }]}
      />
      <BwjPageContent contentDescription={'primary content (no subcontent)'}>
        <BwjGrid>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12}>
              <BwjTextField
                id={formService.description.id}
                required={true}
                label={formService.description.description}
                onUpdate={formService.description.onUpdate}
                input={formModel.description}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12} large={6}>
              <BwjSingleSelectField<IDoorSizeOption>
                id={formService.doorSize.id}
                required={true}
                label={formService.doorSize.description}
                onUpdate={formService.doorSize.onUpdate}
                input={formModel.doorSize}
                options={formService.doorSize.options}
                optionDisplayKey={'description'}
                optionValueKey={'id'}
              />
            </Col>
            <Col small={12} large={6}>
              <BwjSingleSelectField<IStormDoorHandleTypeOption>
                id={formService.validHandlesets.id}
                required={true}
                label={formService.validHandlesets.description}
                onUpdate={formService.validHandlesets.onUpdate}
                input={formModel.validHandlesets}
                options={formService.validHandlesets.options}
                optionDisplayKey={'description'}
                optionValueKey={'id'}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={6} large={3}>
              <BwjCheckbox
                id={formService.includesHandle.id}
                label={formService.includesHandle.description}
                checked={formModel.includesHandle}
                onChange={formService.includesHandle.onUpdate}
              />
            </Col>
            <Col small={6} large={3}>
              <BwjCheckbox
                id={formService.cutdownPossible.id}
                label={formService.cutdownPossible.description}
                checked={formModel.cutdownPossible}
                onChange={formService.cutdownPossible.onUpdate}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12}>
              <div id={formService.imageUrls.id}>
                <Text variant='medium' style={{ fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>{formService.imageUrls.description}</Text>
                <div>
                  <BwjImageBrowser
                    id={`${formService.imageUrls.id}-browser`}
                    images={formModel.imageUrls.value.map(x => { return { src: x }; })}
                    displayIndex={primaryImageDisplayIndex}
                    loadImages={true}
                    imageWidth={400}
                    contextButton={{
                      icon: 'Delete',
                      onClick: onPrimaryImageRemoved
                    }}
                    onDisplayIndexChanged={index => setPrimaryImageDisplayIndex(index)}
                  />
                </div>
              </div>
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12} large={6}>
              <Text variant='medium' style={{ fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>Product variations</Text>
              {door.colorVariants.map(v =>
              <LowesProductVariantListItem
                key={v.omniItemId}
                variant={v}
                onClick={onVariantSelected}
              />)}
            </Col>
            <Col small={12} large={6}>
              <BwjTextField
                id={formService.variantColorFamilies.id}
                required={true}
                label={formService.variantColorFamilies.description}
                onUpdate={formService.variantColorFamilies.onUpdate}
                input={formModel.variantColorFamilies}
                multiline={true}
              />
              <Text variant='small' style={{ display: 'block' }}>{colorFamilyHint}</Text>
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12} large={6}>
              <BwjTextField
                id={formService.sellingPoints.id}
                required={false}
                label={formService.sellingPoints.description}
                onUpdate={formService.sellingPoints.onUpdate}
                input={formModel.sellingPoints}
                multiline={true}
              />
            </Col>
            <Col small={12} large={6}>
              <BwjTextField
                id={formService.productReviewSummary.id}
                required={false}
                label={formService.productReviewSummary.description}
                onUpdate={formService.productReviewSummary.onUpdate}
                input={formModel.productReviewSummary}
                multiline={true}
              />
            </Col>
          </Row>
        </BwjGrid>
      </BwjPageContent>
    </BwjViewManager>
  );
};
